/**
 * Substituir sensor — troca de hardware preservando o histórico.
 *
 * Na mão eram três passos com ordem obrigatória (cadastrar, vincular, dar
 * baixa) e errar a ordem tem consequência silenciosa: dar baixa cedo demais
 * corta o histórico antes da hora, e deixar o sensor antigo mudo e ativo
 * congela a avaliação de energia daquele ativo sem avisar ninguém.
 *
 * O modal mostra a PRÉVIA antes de qualquer mudança — o servidor devolve o
 * que vai acontecer sem alterar nada enquanto `confirmar` for falso. Abrir o
 * modal não pode ter efeito colateral no cadastro.
 */

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AlertTriangle, ArrowRight, Check, History } from "lucide-react";

import { devicesApi } from "@/api/devices";
import { apiErrorMessage } from "@/api/client";
import { Modal } from "@/components/Modal";
import { Button } from "@/components/Button";
import { Field, Input, Select } from "@/components/Field";
import type { Device, DeviceReplaceOut, OrphanDevice } from "@/types";

interface Props {
  /** Sensor que sai. `null` fecha o modal. */
  sai: Device | null;
  /** Pré-seleciona quem entra — usado ao vir da lista de não vinculados. */
  entraLocalId?: string | null;
  /** Candidatos já cadastrados: mesmo ativo, sem equipamento. */
  candidatos: Device[];
  orfaos: OrphanDevice[];
  onClose: () => void;
  onDone: () => void;
}

/** `datetime-local` quer 'YYYY-MM-DDTHH:mm' na hora local, sem timezone. */
function agoraLocal(): string {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

export function DeviceReplaceModal({
  sai, entraLocalId, candidatos, orfaos, onClose, onDone,
}: Props) {
  // "d:<uuid>" para device cadastrado, "o:<local_id>" para não vinculado.
  const [escolha, setEscolha] = useState("");
  const [quando, setQuando] = useState(agoraLocal);

  useEffect(() => {
    if (!sai) return;
    setEscolha(entraLocalId ? `o:${entraLocalId}` : "");
    setQuando(agoraLocal());
  }, [sai, entraLocalId]);

  const orfaosDoSite = useMemo(
    () => orfaos.filter((o) => !candidatos.some((c) => c.local_id === o.local_id)),
    [orfaos, candidatos],
  );

  // O `Select` do projeto recebe lista, não children, então o agrupamento vai
  // como prefixo no rótulo — menos bonito que `optgroup` e honesto sobre a
  // origem de cada candidato, que é o que importa aqui.
  const opcoes = useMemo(() => [
    ...candidatos.map((d) => ({
      value: `d:${d.id}`,
      label: `Cadastrado · ${d.name} (${d.local_id})`,
    })),
    ...orfaosDoSite.map((o) => ({
      value: `o:${o.local_id}`,
      label: `Sem cadastro · ${o.local_id}`,
    })),
  ], [candidatos, orfaosDoSite]);

  const corpo = useMemo(() => {
    if (!escolha) return null;
    const tipo = escolha.slice(0, 1);
    const valor = escolha.slice(2);
    const efetivo = quando ? new Date(quando).toISOString() : undefined;
    return tipo === "d"
      ? { novo_device_id: valor, efetivo_em: efetivo }
      : { novo_local_id: valor, efetivo_em: efetivo };
  }, [escolha, quando]);

  const previa = useQuery<DeviceReplaceOut>({
    queryKey: ["device-replace-preview", sai?.id, escolha, quando],
    queryFn: () => devicesApi.replace(sai!.id, { ...corpo!, confirmar: false }),
    enabled: !!sai && !!corpo,
    retry: false,
  });

  const aplicar = useMutation({
    mutationFn: () => devicesApi.replace(sai!.id, { ...corpo!, confirmar: true }),
    onSuccess: onDone,
  });

  const perdas = previa.data?.mudancas.filter((m) => m.tipo === "perde") ?? [];
  const ganhos = previa.data?.mudancas.filter((m) => m.tipo === "ganha") ?? [];

  return (
    <Modal
      open={!!sai}
      onClose={onClose}
      title="Substituir sensor"
      description={sai ? `${sai.name} · ${sai.local_id}` : undefined}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={aplicar.isPending}>
            Cancelar
          </Button>
          <Button
            onClick={() => aplicar.mutate()}
            disabled={!previa.data || aplicar.isPending}
            loading={aplicar.isPending}
          >
            Substituir
          </Button>
        </>
      }
    >
      {sai && (
        <div className="space-y-4">
          <Field label="Sensor que entra" required>
            <Select
              value={escolha}
              onChange={(e) => setEscolha(e.target.value)}
              placeholder="Selecione…"
              options={opcoes}
            />
          </Field>

          <Field
            label="Troca vale a partir de"
            hint="As leituras do sensor que sai continuam contando até esta data."
          >
            <Input
              type="datetime-local"
              value={quando}
              onChange={(e) => setQuando(e.target.value)}
            />
          </Field>

          {previa.isError && (
            <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
              {apiErrorMessage(previa.error)}
            </div>
          )}

          {previa.data && (
            <div className="space-y-3 rounded-lg border border-surface-200 bg-surface-50 p-3 dark:border-slate-700 dark:bg-slate-800/40">
              <div className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                <span className="font-medium">{previa.data.sai_nome}</span>
                <ArrowRight className="h-4 w-4 text-slate-400" />
                <span className="font-medium">{previa.data.entra_nome}</span>
                <span className="text-slate-500 dark:text-slate-400">
                  em {previa.data.equipment_nome}
                </span>
              </div>

              <div className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-300">
                <History className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                <span>
                  {previa.data.leituras_preservadas.toLocaleString("pt-BR")} leituras
                  do sensor atual continuam no histórico do ativo.
                </span>
              </div>

              {/* O aviso que justifica o modal existir: sem ele, a capacidade
                  some sem ninguém perceber, e a descoberta vem no dia em que
                  ela faria falta. */}
              {perdas.length > 0 && (
                <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2.5 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <div>
                    <div className="font-medium">O sensor novo não faz:</div>
                    <ul className="mt-1 list-disc pl-4">
                      {perdas.map((m) => <li key={m.descricao}>{m.descricao}</li>)}
                    </ul>
                    <div className="mt-1 opacity-80">
                      Revise os alertas deste ativo depois da troca.
                    </div>
                  </div>
                </div>
              )}

              {ganhos.length > 0 && (
                <div className="flex items-start gap-2 text-sm text-emerald-700 dark:text-emerald-300">
                  <Check className="mt-0.5 h-4 w-4 shrink-0" />
                  <div>
                    <div className="font-medium">O sensor novo passa a:</div>
                    <ul className="mt-1 list-disc pl-4">
                      {ganhos.map((m) => <li key={m.descricao}>{m.descricao}</li>)}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          )}

          {aplicar.isError && (
            <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
              {apiErrorMessage(aplicar.error)}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
